import React from 'react'
import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Activity, Zap, AlertTriangle, CheckCircle2, RefreshCw, Clock } from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { formatRelativeDate, formatDate } from '@/lib/utils'

// ── Read-only Automation Health (Slice 1 of observability) ─────────────────
// Surfaces scheduled-task health: heartbeat liveness, daily-sweep chain
// status, failed tasks, recent activity. Strictly READ-ONLY — no mutations,
// no re-run/cancel affordances (those land in later slices). Owner-gated at
// the route (OwnerRoute) and again here (defense in depth).

const HEARTBEAT_STALE_MS = 15 * 60 * 1000 // 15 min — heartbeat fires every 5

// Friendly labels for the scheduled_tasks.task_type enum.
const TASK_TYPE_LABEL: Record<string, string> = {
  invoice_recurring_generate_sweep: 'Recurring invoice generation',
  invoice_reminder_sweep: 'Payment reminders',
  invoice_overdue_sweep: 'Overdue check',
  vendor_accept_reminder: 'Vendor accept reminder',
  vendor_accept_escalation: 'Vendor accept escalation',
  vendor_pre_job_email: 'Vendor pre-job email',
  vendor_quote_followup_daily: 'Vendor quote follow-up',
  vendor_en_route_notify: 'Vendor en-route notify',
  test_ping: 'Test ping',
}
const labelFor = (t: string) => TASK_TYPE_LABEL[t] ?? t

// Duration: seconds w/ one decimal when >= 1s, else raw ms; null → em-dash.
const fmtDuration = (ms: number | null) =>
  ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`

// The three daily self-rescheduling sweep chains. A chain is "alive" when it
// has a future pending row queued; future_pending=0 means the chain stopped.
const SWEEP_TYPES: { type: string; label: string }[] = [
  { type: 'invoice_overdue_sweep', label: 'Overdue check' },
  { type: 'invoice_reminder_sweep', label: 'Payment reminders' },
  { type: 'invoice_recurring_generate_sweep', label: 'Recurring invoice generation' },
]

// Local status→token map. The shared getStatusColor maps `sent`→info and has
// no `failed`; this page wants the task-queue semantics below.
const TASK_STATUS_TOKEN: Record<string, { bg: string; text: string; border: string }> = {
  sent: { bg: 'var(--positive-dim)', text: 'var(--positive)', border: 'var(--positive-border)' },
  failed: { bg: 'var(--danger-dim)', text: 'var(--danger)', border: 'var(--danger-border)' },
  pending: { bg: 'var(--info-dim)', text: 'var(--info)', border: 'var(--info-border)' },
  cancelled: { bg: 'var(--hover-bg)', text: 'var(--ink-tertiary)', border: 'var(--border-subtle)' },
}
const statusToken = (s: string) => TASK_STATUS_TOKEN[s] ?? TASK_STATUS_TOKEN.cancelled

interface HeartbeatStatus {
  last_run_at: string | null
  runs_24h: number
  failed_24h: number
}
interface ScheduledTaskRow {
  id: string
  task_type: string
  status: string
  due_at: string
  sent_at: string | null
  attempts: number
  last_error: string | null
}
interface ProcessorRun {
  ran_at: string
  duration_ms: number | null
  processed: number
  sent: number
  cancelled: number
  failed: number
  retried: number
  skipped: number
  ok: boolean
  error: string | null
}

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 16,
  padding: '20px 22px',
}

function Pill({ tone, children }: { tone: { bg: string; text: string; border: string }; children: React.ReactNode }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 100, fontSize: 11, fontWeight: 700,
      background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`,
      whiteSpace: 'nowrap',
    }}>
      {children}
    </span>
  )
}

const POSITIVE = { bg: 'var(--positive-dim)', text: 'var(--positive)', border: 'var(--positive-border)' }
const DANGER = { bg: 'var(--danger-dim)', text: 'var(--danger)', border: 'var(--danger-border)' }

function CardTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
      {icon}
      <h2 style={{ fontSize: 14, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.01em' }}>
        {children}
      </h2>
    </div>
  )
}

export default function AutomationHealth() {
  const profile = useAuth(s => s.profile)
  const workspace = useAuth(s => s.workspace)

  // Defense in depth — the route is OwnerRoute-gated, but re-check here.
  if (!profile?.is_owner) return <Navigate to="/app" replace />

  const heartbeatQ = useQuery({
    queryKey: ['automation-heartbeat'],
    queryFn: async (): Promise<HeartbeatStatus | null> => {
      const { data, error } = await supabase.rpc('get_automation_heartbeat_status')
      if (error) throw error
      const row = Array.isArray(data) ? data[0] : data
      return (row as HeartbeatStatus) ?? null
    },
    enabled: !!workspace?.id,
  })

  const recentQ = useQuery({
    queryKey: ['automation-recent', workspace?.id],
    queryFn: async (): Promise<ScheduledTaskRow[]> => {
      const { data, error } = await supabase
        .from('scheduled_tasks')
        .select('id, task_type, status, due_at, sent_at, attempts, last_error')
        .order('due_at', { ascending: false })
        .limit(25)
      if (error) throw error
      return (data ?? []) as ScheduledTaskRow[]
    },
    enabled: !!workspace?.id,
  })

  const failedQ = useQuery({
    queryKey: ['automation-failed', workspace?.id],
    queryFn: async (): Promise<ScheduledTaskRow[]> => {
      const { data, error } = await supabase
        .from('scheduled_tasks')
        .select('id, task_type, status, due_at, sent_at, attempts, last_error')
        .eq('status', 'failed')
        .order('due_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as ScheduledTaskRow[]
    },
    enabled: !!workspace?.id,
  })

  const chainQ = useQuery({
    queryKey: ['automation-chains', workspace?.id],
    queryFn: async (): Promise<Record<string, number>> => {
      const { data, error } = await supabase
        .from('scheduled_tasks')
        .select('task_type')
        .eq('status', 'pending')
        .gt('due_at', new Date().toISOString())
        .in('task_type', SWEEP_TYPES.map(s => s.type))
      if (error) throw error
      const counts: Record<string, number> = {}
      for (const row of (data ?? []) as { task_type: string }[]) {
        counts[row.task_type] = (counts[row.task_type] ?? 0) + 1
      }
      return counts
    },
    enabled: !!workspace?.id,
  })

  const runsQ = useQuery({
    queryKey: ['processor-runs', workspace?.id],
    queryFn: async (): Promise<ProcessorRun[]> => {
      const { data, error } = await supabase.rpc('get_processor_run_history', { p_limit: 25 })
      if (error) throw error
      return (data ?? []) as ProcessorRun[]
    },
    enabled: !!workspace?.id,
  })

  const refreshAll = () => {
    heartbeatQ.refetch()
    recentQ.refetch()
    failedQ.refetch()
    chainQ.refetch()
    runsQ.refetch()
  }

  // Heartbeat verdict.
  const hb = heartbeatQ.data
  const lastRunMs = hb?.last_run_at ? new Date(hb.last_run_at).getTime() : null
  const heartbeatHealthy = lastRunMs != null && (Date.now() - lastRunMs) <= HEARTBEAT_STALE_MS

  const failed = failedQ.data ?? []
  const recent = recentQ.data ?? []
  const chains = chainQ.data ?? {}
  const runs = runsQ.data ?? []

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1200 }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}
      >
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: '0 0 4px' }}>
            Automation Health
          </h1>
          <p style={{ fontSize: 14, color: 'var(--ink-tertiary)', margin: 0 }}>
            Background job &amp; billing automation status
          </p>
        </div>
        <button
          onClick={refreshAll}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'var(--field-bg)', border: '1px solid var(--border-default)',
            borderRadius: 10, padding: '8px 14px', cursor: 'pointer',
            color: 'var(--ink-secondary)', fontSize: 13, fontWeight: 600,
          }}
        >
          <RefreshCw style={{ width: 14, height: 14 }} />
          Refresh
        </button>
      </motion.div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 28 }}>
        {/* (a) HEARTBEAT — most prominent */}
        <div style={{
          ...cardStyle,
          borderColor: heartbeatQ.isLoading ? 'var(--border-subtle)' : (heartbeatHealthy ? 'var(--positive-border)' : 'var(--danger-border)'),
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: heartbeatHealthy ? 'var(--positive-dim)' : 'var(--danger-dim)',
              }}>
                {heartbeatHealthy
                  ? <CheckCircle2 style={{ width: 20, height: 20, color: 'var(--positive)' }} />
                  : <AlertTriangle style={{ width: 20, height: 20, color: 'var(--danger)' }} />}
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: 'var(--ink-primary)' }}>Cron heartbeat</span>
                  {heartbeatQ.isLoading ? null : heartbeatHealthy
                    ? <Pill tone={POSITIVE}><CheckCircle2 style={{ width: 11, height: 11 }} /> Healthy</Pill>
                    : <Pill tone={DANGER}><AlertTriangle style={{ width: 11, height: 11 }} /> Heartbeat may be stopped</Pill>}
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink-tertiary)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Clock style={{ width: 12, height: 12 }} />
                  {heartbeatQ.isLoading ? 'Checking…'
                    : heartbeatQ.isError ? 'Unable to read heartbeat status'
                    : `Last run ${formatRelativeDate(hb?.last_run_at)}`}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <Stat label="Runs (24h)" value={heartbeatQ.isLoading ? '—' : String(hb?.runs_24h ?? 0)} />
              <Stat
                label="Failed (24h)"
                value={heartbeatQ.isLoading ? '—' : String(hb?.failed_24h ?? 0)}
                tone={(hb?.failed_24h ?? 0) > 0 ? 'var(--danger)' : 'var(--ink-primary)'}
              />
            </div>
          </div>
          {!heartbeatHealthy && !heartbeatQ.isLoading && !heartbeatQ.isError && (
            <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 10, background: 'var(--danger-dim)', border: '1px solid var(--danger-border)', fontSize: 13, color: 'var(--danger)' }}>
              The processor cron has not run in over 15 minutes. While stopped, no reminders, overdue checks, or recurring invoices are generated.
            </div>
          )}
        </div>

        {/* (b) CHAIN-ALIVE */}
        <div style={cardStyle}>
          <CardTitle icon={<Zap style={{ width: 16, height: 16, color: 'var(--ink-tertiary)' }} />}>Daily sweep chains</CardTitle>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SWEEP_TYPES.map(s => {
              const count = chains[s.type] ?? 0
              const alive = count > 0
              return (
                <div key={s.type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--hover-bg)' }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-secondary)' }}>{s.label}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{count} queued</span>
                    {chainQ.isLoading ? null : alive
                      ? <Pill tone={POSITIVE}><CheckCircle2 style={{ width: 11, height: 11 }} /> Scheduled</Pill>
                      : <Pill tone={DANGER}><AlertTriangle style={{ width: 11, height: 11 }} /> No future run — chain may be stopped</Pill>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* (c) FAILED TASKS */}
        <div style={cardStyle}>
          <CardTitle icon={<AlertTriangle style={{ width: 16, height: 16, color: 'var(--ink-tertiary)' }} />}>
            Failed tasks{failed.length > 0 ? ` (${failed.length})` : ''}
          </CardTitle>
          {failedQ.isLoading ? (
            <div style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>Loading…</div>
          ) : failed.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--positive)' }}>
              <CheckCircle2 style={{ width: 15, height: 15 }} /> No failed tasks
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {failed.map(t => (
                <div key={t.id} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '10px 12px', borderRadius: 10, background: 'var(--danger-dim)', border: '1px solid var(--danger-border)' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{labelFor(t.task_type)}</div>
                    {t.last_error && (
                      <div title={t.last_error} style={{ fontSize: 12, color: 'var(--danger)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 640 }}>
                        {t.last_error}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>{formatRelativeDate(t.due_at)}</div>
                    <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 2 }}>{t.attempts} attempt{t.attempts === 1 ? '' : 's'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* (d) RECENT ACTIVITY */}
        <div style={cardStyle}>
          <CardTitle icon={<Activity style={{ width: 16, height: 16, color: 'var(--ink-tertiary)' }} />}>Recent activity</CardTitle>
          {recentQ.isLoading ? (
            <div style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>Loading…</div>
          ) : recent.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>No scheduled tasks yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* header row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.9fr 1fr 1fr 0.5fr', gap: 12, padding: '6px 10px', fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <span>Task</span><span>Status</span><span>Due</span><span>Sent</span><span style={{ textAlign: 'right' }}>Tries</span>
              </div>
              {recent.map(t => (
                <div key={t.id} style={{ display: 'grid', gridTemplateColumns: '1.6fr 0.9fr 1fr 1fr 0.5fr', gap: 12, alignItems: 'center', padding: '10px', borderTop: '1px solid var(--border-subtle)', fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{labelFor(t.task_type)}</span>
                  <span><Pill tone={statusToken(t.status)}>{t.status}</Pill></span>
                  <span style={{ color: 'var(--ink-tertiary)' }}>{formatRelativeDate(t.due_at)}</span>
                  <span style={{ color: 'var(--ink-tertiary)' }}>{t.sent_at ? formatRelativeDate(t.sent_at) : '—'}</span>
                  <span style={{ textAlign: 'right', color: 'var(--ink-tertiary)' }}>{t.attempts}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* (e) RECENT PROCESSOR RUNS — per-tick run-log (Slice 2). Each row = one
            cron tick's BatchStats; the status cell title= carries the full count
            breakdown + any error so hidden counts stay accessible. */}
        <div style={cardStyle}>
          <CardTitle icon={<Clock style={{ width: 16, height: 16, color: 'var(--ink-tertiary)' }} />}>Recent processor runs</CardTitle>
          {runsQ.isLoading ? (
            <div style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>Loading…</div>
          ) : runsQ.isError ? (
            <div style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>Unable to read run history</div>
          ) : runs.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>No runs recorded yet</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {/* header row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 0.7fr 0.6fr 0.6fr 0.8fr', gap: 12, padding: '6px 10px', fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <span>Ran</span><span>Duration</span>
                <span style={{ textAlign: 'right' }}>Processed</span>
                <span style={{ textAlign: 'right' }}>Sent</span>
                <span style={{ textAlign: 'right' }}>Failed</span>
                <span style={{ textAlign: 'right' }}>Status</span>
              </div>
              {runs.map((r, i) => {
                const breakdown =
                  `processed ${r.processed} · sent ${r.sent} · cancelled ${r.cancelled} · `
                  + `failed ${r.failed} · retried ${r.retried} · skipped ${r.skipped}`
                  + (r.error ? `\nerror: ${r.error}` : '')
                return (
                  <div key={`${r.ran_at}-${i}`} style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr 0.7fr 0.6fr 0.6fr 0.8fr', gap: 12, alignItems: 'center', padding: '10px', borderTop: '1px solid var(--border-subtle)', fontSize: 13 }}>
                    <span style={{ color: 'var(--ink-secondary)' }} title={formatDate(r.ran_at, 'MMM d, HH:mm:ss')}>{formatRelativeDate(r.ran_at)}</span>
                    <span style={{ color: 'var(--ink-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{fmtDuration(r.duration_ms)}</span>
                    <span style={{ textAlign: 'right', color: 'var(--ink-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{r.processed}</span>
                    <span style={{ textAlign: 'right', color: 'var(--ink-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{r.sent}</span>
                    <span style={{ textAlign: 'right', color: r.failed > 0 ? 'var(--danger)' : 'var(--ink-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{r.failed}</span>
                    <span style={{ textAlign: 'right' }} title={breakdown}>
                      {r.ok
                        ? <Pill tone={POSITIVE}><CheckCircle2 style={{ width: 11, height: 11 }} /> ok</Pill>
                        : <Pill tone={DANGER}><AlertTriangle style={{ width: 11, height: 11 }} /> failed</Pill>}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: tone ?? 'var(--ink-primary)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 2 }}>{label}</div>
    </div>
  )
}
